import React from 'react';
import { Button } from '@fiverr-private/button';
import { Center, Divider, Stack } from '@fiverr-private/layout_components';
import { Heading, Typography } from '@fiverr-private/typography';
import { BillingCycle, PricingDetails, SubscriptionTier } from '../../../../type';
import { appTranslate } from '../../../i18n';
import { PackageDetails } from '../types';
import { useIsDiscountPage } from '../../../hooks';
import { DiscountedPricing } from './DiscountedPricing';
import { RegularPricing } from './RegularPricing';

interface PackageCardHeaderProps extends Pick<PackageDetails, 'vendor' | 'shortInfo'> {
    tierName: PackageDetails['id'];
    pricingDetails?: PricingDetails;
    onBuyPackage: () => void;
}

export const PackageCardHeader = ({
    pricingDetails,
    shortInfo,
    tierName,
    vendor,
    onBuyPackage,
}: PackageCardHeaderProps) => {
    const isDiscountPage = useIsDiscountPage();

    return (
        <Stack rowGap="4" direction="column">
            <Stack rowGap="1" direction="column">
                <Typography color="grey_1200" size="b_lg">
                    {vendor.title}
                </Typography>
                <Heading as="h5" size="h_sm">
                    {vendor.label}
                </Heading>
            </Stack>

            {isDiscountPage ? (
                <DiscountedPricing pricingDetails={pricingDetails} />
            ) : (
                <RegularPricing tierName={tierName} pricingDetails={pricingDetails} />
            )}

            <Stack rowGap="3" direction="column">
                <Button
                    disabled={!pricingDetails?.price}
                    variant={tierName === SubscriptionTier.Standard ? 'filled' : 'outline'}
                    size="sm"
                    fullWidth
                    style={{ minWidth: '220px' }}
                    onClick={onBuyPackage}
                >
                    {appTranslate('common.actions.buy')}
                </Button>

                <Stack
                    wrap="wrap"
                    columnGap="2"
                    rowGap="1"
                    withDivider
                    dividerProps={{
                        variant: 'dot',
                        borderColor: 'grey_800',
                    }}
                >
                    {pricingDetails && (
                        <Typography color="grey_900" size="b_xs" whiteSpace="nowrap">
                            {pricingDetails.billingCycle === BillingCycle.MONTHLY &&
                                appTranslate('package_details.billing_cycle.options.monthly')}
                            {pricingDetails.billingCycle === BillingCycle.YEARLY &&
                                appTranslate('package_details.billing_cycle.options.yearly')}
                        </Typography>
                    )}

                    {shortInfo.map((info) => (
                        <Typography key={info} color="grey_900" size="b_xs" whiteSpace="nowrap">
                            {info}
                        </Typography>
                    ))}
                </Stack>
            </Stack>

            <Center>
                <Divider variant="line" />
            </Center>
        </Stack>
    );
};
